import React, { useState, useEffect } from 'react';
import { Plus, Calendar, MapPin, Globe, Edit, Trash2, ExternalLink } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminEvents = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', event_date: '', event_time: '', timezone: 'Europe/London', location: '', is_virtual: false, host_org: '', event_type: 'keynote', status: 'upcoming', description: '', registration_link: '' });

  const { token } = useAuth();

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/cms/events`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const data = await res.json(); setEvents(data.events); }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetchEvents(); }, []);

  const handleSave = async () => {
    const method = editing ? 'PUT' : 'POST';
    const url = editing ? `${API_URL}/api/cms/events/${editing}` : `${API_URL}/api/cms/events`;
    await fetch(url, { method, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(form) });
    setShowForm(false);
    setEditing(null);
    setForm({ title: '', event_date: '', event_time: '', timezone: 'Europe/London', location: '', is_virtual: false, host_org: '', event_type: 'keynote', status: 'upcoming', description: '', registration_link: '' });
    fetchEvents();
  };

  const handleEdit = (event) => {
    setForm(event);
    setEditing(event.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this event?')) return;
    await fetch(`${API_URL}/api/cms/events/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    fetchEvents();
  };

  const inputClass = "w-full bg-navy border border-cream/10 rounded-lg px-3 py-2 text-sm text-cream placeholder-cream/20 focus:outline-none focus:ring-1 focus:ring-gold/30";

  return (
    <div data-testid="admin-events">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-serif text-2xl text-cream font-semibold">Events</h1>
          <p className="text-cream/30 text-sm mt-1">Speaking & workshop events</p>
        </div>
        <button onClick={() => { setShowForm(true); setEditing(null); setForm({ title: '', event_date: '', event_time: '', timezone: 'Europe/London', location: '', is_virtual: false, host_org: '', event_type: 'keynote', status: 'upcoming', description: '', registration_link: '' }); }} className="inline-flex items-center gap-2 px-4 py-2 bg-gold text-navy text-sm font-semibold rounded-lg hover:bg-gold-light transition-all" data-testid="new-event-btn">
          <Plus size={16} /> New Event
        </button>
      </div>

      {showForm && (
        <div className="bg-navy border border-gold/[0.06] rounded-xl p-6 mb-6">
          <h2 className="text-cream text-sm font-semibold mb-4">{editing ? 'Edit Event' : 'New Event'}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div><label className="block text-[10px] text-cream/20 mb-1 uppercase tracking-wider">Title *</label><input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputClass} placeholder="Event title" /></div>
            <div><label className="block text-[10px] text-cream/20 mb-1 uppercase tracking-wider">Event Type</label>
              <select value={form.event_type} onChange={(e) => setForm({ ...form, event_type: e.target.value })} className={inputClass}><option value="keynote">Keynote</option><option value="workshop">Workshop</option><option value="panel">Panel</option><option value="conference">Conference</option><option value="retreat">Retreat</option></select></div>
            <div><label className="block text-[10px] text-cream/20 mb-1 uppercase tracking-wider">Date</label><input type="date" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} className={inputClass} /></div>
            <div><label className="block text-[10px] text-cream/20 mb-1 uppercase tracking-wider">Location</label><input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className={inputClass} placeholder="Location or Virtual" /></div>
            <div><label className="block text-[10px] text-cream/20 mb-1 uppercase tracking-wider">Host Organisation</label><input value={form.host_org} onChange={(e) => setForm({ ...form, host_org: e.target.value })} className={inputClass} placeholder="Organisation name" /></div>
            <div><label className="block text-[10px] text-cream/20 mb-1 uppercase tracking-wider">Status</label>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputClass}><option value="draft">Draft</option><option value="upcoming">Upcoming</option><option value="past">Past</option><option value="private">Private</option></select></div>
          </div>
          <div className="mb-4"><label className="block text-[10px] text-cream/20 mb-1 uppercase tracking-wider">Description</label><textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className={`${inputClass} resize-none`} /></div>
          <div className="mb-4"><label className="block text-[10px] text-cream/20 mb-1 uppercase tracking-wider">Registration Link</label><input value={form.registration_link || ''} onChange={(e) => setForm({ ...form, registration_link: e.target.value })} className={inputClass} placeholder="https://..." /></div>
          <div className="flex gap-2">
            <button onClick={handleSave} className="px-4 py-2 bg-gold text-navy text-sm font-semibold rounded-lg hover:bg-gold-light">Save Event</button>
            <button onClick={() => { setShowForm(false); setEditing(null); }} className="px-4 py-2 text-cream/30 text-sm hover:text-cream/50">Cancel</button>
          </div>
        </div>
      )}

      {loading ? <div className="py-16 text-center text-cream/20 text-sm">Loading...</div> : events.length === 0 ? (
        <div className="py-16 text-center bg-navy border border-dashed border-gold/[0.06] rounded-xl">
          <Calendar size={32} className="mx-auto text-cream/10 mb-3" />
          <p className="text-cream/30 text-sm">No events yet</p>
        </div>
      ) : (
        <div className="space-y-2">
          {events.map((ev) => (
            <div key={ev.id} className="bg-navy border border-gold/[0.06] rounded-xl p-4 flex items-center justify-between hover:border-gold/15 transition-all">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-navy-deep rounded-lg flex flex-col items-center justify-center">
                  <Calendar size={14} className="text-gold/40" />
                  <span className="text-cream/30 text-[9px] mt-0.5">{ev.event_date ? new Date(ev.event_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '-'}</span>
                </div>
                <div>
                  <h3 className="text-cream text-sm font-medium">{ev.title}</h3>
                  <div className="flex items-center gap-3 mt-1">
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded ${ev.status === 'upcoming' ? 'bg-green-500/10 text-green-400' : ev.status === 'past' ? 'bg-cream/[0.03] text-cream/20' : 'bg-amber-500/10 text-amber-300'}`}>{ev.status}</span>
                    {ev.location && <span className="text-cream/20 text-xs flex items-center gap-1"><MapPin size={10} />{ev.location}</span>}
                    <span className="text-cream/15 text-xs">{ev.event_type}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => handleEdit(ev)} className="p-2 text-cream/20 hover:text-gold transition-colors"><Edit size={14} /></button>
                <button onClick={() => handleDelete(ev.id)} className="p-2 text-cream/20 hover:text-red-400 transition-colors"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminEvents;
